// src/components/StaffListView.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useOutletContext } from 'react-router-dom';
import apiService from '../services/api';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { formatDate } from '../utils';

// Helper to format data for the small trend charts
const formatTrendData = (weeklyScores) => {
    if (!Array.isArray(weeklyScores) || weeklyScores.length === 0) {
        return [];
    }
    return weeklyScores.map(item => ({
        // Format week for display, or use as is if already formatted
        week: item.week ? (item.week.year ? `${item.week.year}-W${item.week.week}` : formatDate(item.week)) : 'N/A',
        score: item.score
    })).sort((a,b) => new Date(a.week) - new Date(b.week)); // Ensure sorted by week
};

// Get score badge styles based on score value
const getScoreBadgeStyle = (score) => {
    if (score === null || score === undefined) {
        return { backgroundColor: 'var(--pallium-stroke-lighter)', color: 'var(--pallium-text-secondary)' };
    }
    if (score >= 4) {
        return { backgroundColor: 'rgba(19, 185, 85, 0.15)', color: 'var(--pallium-success-green)' };
    }
    if (score >= 3) {
        return { backgroundColor: 'rgba(239, 163, 29, 0.15)', color: 'var(--pallium-warning-yellow)' };
    }
    return { backgroundColor: 'rgba(252, 57, 57, 0.15)', color: 'var(--pallium-error-red)' };
};


const StaffListView = () => {
    // Try to get apiService from context, but have a fallback
    let contextApiService;
    try {
        const context = useOutletContext();
        contextApiService = context?.apiService;
    } catch (e) {
        // useOutletContext might not be available if this component is rendered outside an Outlet
        console.warn("useOutletContext not available for StaffListView, falling back to direct import for apiService.");
    }
    const activeApiService = contextApiService || apiService;


    const [staffData, setStaffData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedDepartment, setSelectedDepartment] = useState('');
    const [hideNoData, setHideNoData] = useState(true);
    const navigate = useNavigate();

    const fetchStaffData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await activeApiService.getStaffPerformanceSummaryList();
            
            setStaffData(result.map(record => ({
                id: record.staffId,
                name: record.staffName,
                imageUrl: record.staffImageUrl,
                department: record.department,
                trailingScore: record.trailingScore,
                weeklyScores: formatTrendData(record.weeklyScores || []), 
            })));
        } catch (err) {
            console.error("Error fetching staff list with performance:", err);
            setError("Failed to load staff data. " + err.message);
        } finally {
            setLoading(false);
        }
    }, [activeApiService]);

    useEffect(() => {
        fetchStaffData();
    }, [fetchStaffData]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div 
                    className="animate-spin rounded-full h-12 w-12 border-b-2"
                    style={{ borderColor: 'var(--pallium-primary-purple)' }}
                ></div>
                <span className="ml-3" style={{ color: 'var(--pallium-text-secondary)' }}>
                    Loading staff data...
                </span>
            </div>
        );
    }

    if (error) {
        return (
            <div 
                className="p-4 rounded-md"
                style={{ backgroundColor: 'var(--pallium-alert-error-bg)', color: 'var(--pallium-error-red)' }}
            >
                <strong>Error:</strong> {error} 
                <button 
                    onClick={fetchStaffData} 
                    className="ml-2 text-white font-bold py-1 px-2 rounded text-sm transition-opacity"
                    style={{ backgroundColor: 'var(--pallium-error-red)' }}
                    onMouseOver={(e) => e.target.style.opacity = '0.8'}
                    onMouseOut={(e) => e.target.style.opacity = '1'}
                >
                    Retry
                </button>
            </div>
        );
    }

    if (staffData.length === 0) {
        return (
            <div className="p-4 text-center" style={{ color: 'var(--pallium-text-secondary)' }}>
                No staff members found.
            </div>
        );
    }

    // Get unique departments from staff data
    const departments = [...new Set(staffData.map(s => s.department).filter(d => d))].sort();

    // Filter staff based on selections
    const filteredStaff = staffData.filter(staff => {
        // Filter by department
        if (selectedDepartment && staff.department !== selectedDepartment) {
            return false;
        }
        // Filter out staff with no data if hideNoData is enabled
        if (hideNoData && (staff.trailingScore === null || staff.trailingScore === undefined)) {
            return false;
        }
        return true;
    });

    // Calculate overall company score (average of all staff with data)
    const staffWithScores = filteredStaff.filter(s => s.trailingScore !== null && s.trailingScore !== undefined);
    const overallScore = staffWithScores.length > 0 
        ? staffWithScores.reduce((sum, s) => sum + s.trailingScore, 0) / staffWithScores.length 
        : null;

    return (
        <div className="container mx-auto">
            {/* Header with Overall Score and Filters */}
            <div 
                className="rounded-xl p-6 mb-6"
                style={{ backgroundColor: 'var(--pallium-white)', boxShadow: 'var(--pallium-shadow-md)' }}
            >
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    {/* Overall Score */}
                    <div className="flex items-center gap-4">
                        <div>
                            <h3 
                                className="text-sm font-semibold uppercase tracking-wider mb-1"
                                style={{ color: 'var(--pallium-primary-purple)' }}
                            >
                                Overall {selectedDepartment || 'Company'} Score
                            </h3>
                            <div className="flex items-center gap-2">
                                <span 
                                    className="text-3xl font-bold"
                                    style={getScoreBadgeStyle(overallScore)}
                                >
                                    {overallScore !== null ? overallScore.toFixed(2) : 'N/A'}
                                </span>
                                <span 
                                    className="text-sm"
                                    style={{ color: 'var(--pallium-text-secondary)' }}
                                >
                                    ({staffWithScores.length} staff member{staffWithScores.length !== 1 ? 's' : ''})
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Filters */}
                    <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                        {/* Department Filter */}
                        <div className="flex flex-col">
                            <label 
                                className="text-xs font-semibold mb-1"
                                style={{ color: 'var(--pallium-primary-purple)' }}
                            >
                                DEPARTMENT
                            </label>
                            <select
                                value={selectedDepartment}
                                onChange={(e) => setSelectedDepartment(e.target.value)}
                                className="px-3 py-2 rounded-md border text-sm"
                                style={{ 
                                    borderColor: 'var(--pallium-stroke-light)',
                                    color: 'var(--pallium-text-primary)',
                                    backgroundColor: 'var(--pallium-white)'
                                }}
                            >
                                <option value="">All Departments</option>
                                {departments.map(dept => (
                                    <option key={dept} value={dept}>{dept}</option>
                                ))}
                            </select>
                        </div>

                        {/* Hide No Data Toggle */}
                        <div className="flex items-end">
                            <label className="flex items-center gap-2 cursor-pointer px-3 py-2">
                                <input
                                    type="checkbox"
                                    checked={hideNoData}
                                    onChange={(e) => setHideNoData(e.target.checked)}
                                    className="w-4 h-4 rounded"
                                    style={{ accentColor: 'var(--pallium-primary-purple)' }}
                                />
                                <span 
                                    className="text-sm font-medium"
                                    style={{ color: 'var(--pallium-text-primary)' }}
                                >
                                    Hide staff with no data
                                </span>
                            </label>
                        </div>
                        <div className="flex items-end">
                            <button
                                onClick={() => navigate('/staff/manage')}
                                className="px-4 py-2 text-sm font-medium rounded-lg text-white"
                                style={{ backgroundColor: 'var(--pallium-primary-purple)' }}
                            >
                                Manage Staff
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Staff Table */}
            {filteredStaff.length === 0 ? (
                <div 
                    className="p-6 text-center rounded-xl"
                    style={{ 
                        backgroundColor: 'var(--pallium-white)', 
                        boxShadow: 'var(--pallium-shadow-md)',
                        color: 'var(--pallium-text-secondary)' 
                    }}
                >
                    No staff members match the current filters.
                </div>
            ) : (
            <div 
                className="rounded-xl overflow-hidden"
                style={{ backgroundColor: 'var(--pallium-white)', boxShadow: 'var(--pallium-shadow-md)' }}
                data-testid="staff-list"
            >
                <table className="min-w-full leading-normal">
                    <thead>
                        <tr style={{ backgroundColor: 'var(--pallium-purple-0-5)' }}>
                            <th 
                                className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider"
                                style={{ 
                                    color: 'var(--pallium-primary-purple)',
                                    borderBottom: '2px solid var(--pallium-purple-2)'
                                }}
                            >
                                Staff Name
                            </th>
                            <th 
                                className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider"
                                style={{ 
                                    color: 'var(--pallium-primary-purple)',
                                    borderBottom: '2px solid var(--pallium-purple-2)'
                                }}
                            >
                                Department
                            </th>
                            <th 
                                className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider"
                                style={{ 
                                    color: 'var(--pallium-primary-purple)',
                                    borderBottom: '2px solid var(--pallium-purple-2)'
                                }}
                            >
                                Trailing 2-Wk Score
                            </th>
                            <th 
                                className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider"
                                style={{ 
                                    color: 'var(--pallium-primary-purple)',
                                    borderBottom: '2px solid var(--pallium-purple-2)'
                                }}
                            >
                                Weekly Score Trend (8 wks)
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredStaff.map((staff) => (
                            <tr 
                                key={staff.id} 
                                className="cursor-pointer transition-colors duration-200"
                                style={{ borderBottom: '1px solid var(--pallium-stroke-light)' }}
                                onClick={() => navigate(`/staff/${staff.id}`)}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'var(--pallium-purple-0-5)'}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                data-testid="staff-row"
                            >
                                <td className="px-6 py-4">
                                    <div className="flex items-center">
                                        {staff.imageUrl ? (
                                            <img 
                                                src={staff.imageUrl} 
                                                alt={staff.name} 
                                                className="w-10 h-10 rounded-full mr-3 object-cover" 
                                                onError={(e) => { e.target.onerror = null; e.target.src = `https://placehold.co/40x40/E2E8F0/718096?text=${staff.name.charAt(0)}`; }} 
                                            />
                                        ) : (
                                            <div 
                                                className="w-10 h-10 rounded-full mr-3 flex items-center justify-center font-semibold"
                                                style={{ 
                                                    backgroundColor: 'var(--pallium-purple-1)', 
                                                    color: 'var(--pallium-primary-purple)' 
                                                }}
                                            >
                                                {staff.name ? staff.name.charAt(0) : '?'}
                                            </div>
                                        )}
                                        <Link 
                                            to={`/staff/${staff.id}`} 
                                            className="font-medium hover:underline"
                                            style={{ color: 'var(--pallium-primary-purple)' }}
                                            onClick={(e) => e.stopPropagation()}
                                            data-testid="staff-name"
                                        >
                                            {staff.name || 'N/A'}
                                        </Link>
                                    </div>
                                </td>
                                <td className="px-6 py-4 text-center">
                                    <span 
                                        className="text-sm"
                                        style={{ color: 'var(--pallium-text-primary)' }}
                                    >
                                        {staff.department || 'N/A'}
                                    </span>
                                </td>
                                <td className="px-6 py-4 text-center">
                                    <span 
                                        className="px-3 py-1 text-sm font-semibold rounded-full"
                                        style={getScoreBadgeStyle(staff.trailingScore)}
                                        data-testid="trailing-score"
                                    >
                                        {staff.trailingScore !== null && staff.trailingScore !== undefined 
                                            ? staff.trailingScore.toFixed(2) 
                                            : 'N/A'
                                        }
                                    </span>
                                </td>
                                <td className="px-6 py-4 text-center" data-testid="score-trend">
                                    {staff.weeklyScores && staff.weeklyScores.length > 0 ? (
                                        <div style={{ height: '50px', width: '150px', margin: 'auto' }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <LineChart data={staff.weeklyScores} margin={{ top: 5, right: 5, left: -30, bottom: 5 }}>
                                                    <XAxis dataKey="week" hide />
                                                    <YAxis domain={[0, 5]} hide />
                                                    <Tooltip 
                                                        contentStyle={{ fontSize: '10px', padding: '2px 5px' }}
                                                        formatter={(value) => [value.toFixed(2), "Score"]}
                                                        labelFormatter={(label) => `Week: ${label}`}
                                                    />
                                                    <Line 
                                                        type="monotone" 
                                                        dataKey="score" 
                                                        stroke="var(--pallium-primary-purple)" 
                                                        strokeWidth={2} 
                                                        dot={false} 
                                                    />
                                                </LineChart>
                                            </ResponsiveContainer>
                                        </div>
                                    ) : (
                                        <span style={{ color: 'var(--pallium-icon-gray)', fontSize: '0.75rem' }}>
                                            No trend data
                                        </span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            )}
        </div>
    );
};

export default StaffListView;
